import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate, useParams } from "react-router-dom";
import { WireGuardTunnel } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { fetchWireguardTunnelsForCustomer } from "../Sites/SiteAboutPage/SiteAboutPageUtils/fetchWireguardTunnels";
import { createApi } from "../../utils/createApi";
import { useOnvifScanStore } from "./onvifScanStore/onvifScanStore";
import { startOnvifScan } from "./OnvifScanUtils/startOnvifScan";
import { handleSubmitOnvifCameras } from "./OnvifScanUtils/handleSubmitOnvifCameras";
import { flattenDiscoveredCameras } from "./OnvifScanUtils/flattenDiscoveredCameras";
import { summarizeScanHosts } from "./OnvifScanUtils/summarizeScanHosts";
import { OnvifResultsTable } from "./OnvifResultsTable";
import { OnvifDiscoveredHostsTable } from "./OnvifDiscoveredHostsTable";
import { CameraUnitScanPanel } from "./CameraUnitScanPanel";
import { OnvifScanMode } from "./OnvifScanUtils/onvifScanInterfaces";

const headingSx = {
  fontFamily: "Mulish",
  fontWeight: 700,
  color: "#001943",
};

export type OnvifScanScreenProps = {
  variant?: "page" | "onboarding";
  /**
   * "discover" (default): find new cameras on a WireGuard subnet and create
   * them. "update": re-read the units behind the site's existing cameras over
   * ONVIF and update them in place — no tunnel, no subnet.
   */
  mode?: OnvifScanMode;
  onSuccess?: () => void;
  /** Onboarding: leave discovery when the site group has no tunnels to scan. */
  onNoTunnels?: () => void;
  siteIdOverride?: string;
};

export const OnvifScanScreen = (props: OnvifScanScreenProps) => {
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const siteId = props.siteIdOverride ?? siteIdParam ?? "";
  if (props.mode === "update") {
    // Update mode never touches WireGuard: no tunnel fetch, no subnet, no
    // `wireguard` PATCH. It is a separate component so none of the discovery
    // effects below run.
    return <CameraUnitScanPanel siteId={siteId} onSuccess={props.onSuccess} />;
  }
  return <OnvifDiscoverScreen {...props} />;
};

const OnvifDiscoverScreen = ({
  variant = "page",
  onSuccess,
  onNoTunnels,
  siteIdOverride,
}: OnvifScanScreenProps) => {
  const navigate = useNavigate();
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const siteId = siteIdOverride ?? siteIdParam ?? "";
  const isOnboarding = variant === "onboarding";

  const [tunnels, setTunnels] = React.useState<WireGuardTunnel[]>([]);
  const [tunnelsLoading, setTunnelsLoading] = React.useState(true);
  const [tunnelsError, setTunnelsError] = React.useState("");

  const {
    tunnelId,
    setTunnelId,
    credentials,
    setCredentials,
    scanCredentials,
    subnet,
    setSubnet,
    scanStatus,
    hosts,
    errorMessage,
    selectedKeys,
    setSelectedKeys,
    selectedProfiles,
    setSelectedProfile,
    submitting,
    setSubmitting,
  } = useOnvifScanStore();

  React.useEffect(() => {
    if (!siteId) {
      return;
    }
    let cancelled = false;
    setTunnelsLoading(true);
    // Only existing tunnels in this site's group hierarchy — never provision one.
    fetchWireguardTunnelsForCustomer(siteId)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setTunnels(result);
        if (result.length > 0 && tunnelId == null) {
          setTunnelId(result[0].id);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTunnelsError("Failed to load WireGuard tunnels for this site.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTunnelsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  // Onboarding: show the empty-tunnel warning briefly, then leave for Site About.
  React.useEffect(() => {
    if (!isOnboarding || !onNoTunnels || tunnelsLoading || tunnels.length > 0) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      onNoTunnels();
    }, 2500);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOnboarding, onNoTunnels, tunnelsLoading, tunnels.length]);

  const cameras = React.useMemo(() => flattenDiscoveredCameras(hosts), [hosts]);
  const hostSummary = React.useMemo(() => summarizeScanHosts(hosts), [hosts]);
  // Apply the chosen stream/profile per camera (defaults to the primary).
  const selectedCameras = cameras
    .filter((c) => selectedKeys[c.key])
    .map((c) => {
      const token = selectedProfiles[c.key];
      const profile = token
        ? c.profiles.find((p) => p.token === token)
        : undefined;
      return profile
        ? {
            ...c,
            rtspUri: profile.rtspUri,
            resolution: profile.resolution,
            profileToken: profile.token,
          }
        : c;
    });
  const scanning = scanStatus === "pending";

  const updateCredential = (
    index: number,
    field: "username" | "password",
    value: string
  ) => {
    const next = credentials.map((c, i) =>
      i === index ? { ...c, [field]: value } : c
    );
    setCredentials(next);
  };

  const addCredentialRow = () =>
    setCredentials([...credentials, { username: "", password: "" }]);

  const removeCredentialRow = (index: number) =>
    setCredentials(credentials.filter((_, i) => i !== index));

  const handleScan = () => {
    if (tunnelId == null) {
      return;
    }
    // Auto-select the site's WireGuard endpoint (AZ) from the tunnel being
    // scanned, so the General Info WireGuard selector reflects it without the
    // operator having to set it manually. Best-effort; the scan proceeds either way.
    const scannedServer = tunnels.find((t) => t.id === tunnelId)?.server;
    if (scannedServer != null && siteId) {
      createApi("")
        .patch(`customer/${siteId}/about/`, { wireguard: scannedServer })
        .catch(() => {});
    }
    startOnvifScan({ tunnelId, credentials, subnet, navigate });
  };

  const toggle = (key: string) =>
    setSelectedKeys({ ...selectedKeys, [key]: !selectedKeys[key] });

  const toggleAll = (checked: boolean) => {
    const next: Record<string, boolean> = {};
    cameras.forEach((c) => {
      next[c.key] = checked;
    });
    setSelectedKeys(next);
  };

  const hasCredential = credentials.some(
    (c) => c.username && c.username.trim() !== ""
  );
  const canScan = tunnelId != null && hasCredential && !scanning && !submitting;

  // Onboarding with no tunnels: message only (redirect handled by effect above).
  // Hold an empty frame while tunnels load so the scan form does not flash first.
  if (isOnboarding && (tunnelsLoading || tunnels.length === 0)) {
    if (tunnelsLoading) {
      return null;
    }
    return (
      <p className="absolute first-line:font-black font-mulish text-[20px] right-[40px] left-10 text-center text-[#283E6D] top-80">
        This site&apos;s group has no WireGuard tunnels. A tunnel is required to
        scan the customer network. Rerouting to site&apos;s about page...
      </p>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: isOnboarding ? "100%" : 1100 }}>
      {!isOnboarding && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography sx={{ ...headingSx, fontSize: 22 }}>
            ONVIF Camera Discovery
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(`/sites/aboutsite/${siteId}`)}
            disabled={scanning || submitting}
            sx={{
              fontFamily: "Mulish",
              textTransform: "none",
              backgroundColor: "#283E6D",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#1f2f54" },
            }}
          >
            Skip for now
          </Button>
        </Box>
      )}

      {tunnelsError && (
        <Alert severity="error" sx={{ fontFamily: "Mulish", mb: 2 }}>
          {tunnelsError}
        </Alert>
      )}

      {/* Scan setup — white Paper to match the post-scan result tables */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        {/* Target: WireGuard tunnel */}
        <FormControl sx={{ minWidth: 320, mb: 3 }} size="small">
          <InputLabel id="onvif-tunnel-label" sx={{ fontFamily: "Mulish" }}>
            Target tunnel
          </InputLabel>
          <Select
            labelId="onvif-tunnel-label"
            label="Target tunnel"
            value={tunnelId != null && tunnels.length > 0 ? tunnelId : ""}
            onChange={(e) => setTunnelId(Number(e.target.value))}
            disabled={tunnelsLoading || scanning}
            sx={{ fontFamily: "Mulish" }}
          >
            {tunnels.map((t) => (
              <MenuItem key={t.id} value={t.id} sx={{ fontFamily: "Mulish" }}>
                {t.name}
                {t.subnets && t.subnets.length > 0
                  ? ` — ${t.subnets.join(", ")}`
                  : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!isOnboarding && !tunnelsLoading && tunnels.length === 0 && (
          <Alert severity="warning" sx={{ fontFamily: "Mulish", mb: 2 }}>
            This site&apos;s group has no WireGuard tunnels. A tunnel is
            required to scan the customer network.
          </Alert>
        )}

        {/* ONVIF credentials */}
        <Typography sx={{ ...headingSx, fontSize: 16, mb: 1 }}>
          ONVIF credentials
        </Typography>
        <Stack spacing={1} sx={{ mb: 1 }}>
          {credentials.map((cred, index) => (
            <Stack direction="row" spacing={1} key={index} alignItems="center">
              <TextField
                size="small"
                label="Username"
                value={cred.username}
                onChange={(e) =>
                  updateCredential(index, "username", e.target.value)
                }
                disabled={scanning}
                sx={{ fontFamily: "Mulish" }}
              />
              <TextField
                size="small"
                label="Password"
                type="password"
                value={cred.password}
                onChange={(e) =>
                  updateCredential(index, "password", e.target.value)
                }
                disabled={scanning}
                sx={{ fontFamily: "Mulish" }}
              />
              <IconButton
                aria-label="remove credential"
                onClick={() => removeCredentialRow(index)}
                disabled={credentials.length <= 1 || scanning}
                size="small"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Button
          startIcon={<AddIcon />}
          onClick={addCredentialRow}
          disabled={scanning}
          sx={{
            fontFamily: "Mulish",
            textTransform: "none",
            mb: 3,
            display: "block",
          }}
        >
          Add credential
        </Button>

        {/* Optional subnet override */}
        <Box>
          <TextField
            size="small"
            label="Subnet override (optional)"
            placeholder="e.g. 192.168.1.0/24"
            value={subnet}
            onChange={(e) => setSubnet(e.target.value)}
            disabled={scanning}
            helperText="Defaults to the tunnel's first subnet."
            sx={{ minWidth: 320, fontFamily: "Mulish" }}
          />
        </Box>
      </Paper>

      <Button
        variant="contained"
        onClick={handleScan}
        disabled={!canScan}
        sx={{
          fontFamily: "Mulish",
          textTransform: "none",
          backgroundColor: "#FF8400",
          "&:hover": { backgroundColor: "#E67300" },
        }}
      >
        {scanning ? "Scanning…" : "Scan for cameras"}
      </Button>

      {/* Scan state */}
      {scanning && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3 }}>
          <CircularProgress size={22} />
          <Typography sx={{ fontFamily: "Mulish", color: "#283E6D" }}>
            Scanning the network over the tunnel. This can take up to a few
            minutes…
          </Typography>
        </Stack>
      )}

      {scanStatus === "failed" && errorMessage && (
        <Alert severity="error" sx={{ fontFamily: "Mulish", mt: 3 }}>
          {errorMessage}
        </Alert>
      )}

      {scanStatus === "success" && (
        <Box sx={{ mt: 3 }}>
          {/* A scan can turn up dozens of hosts and yield one camera because the
              rest rejected the credentials supplied. Say so plainly — otherwise
              the operator reads the result as "this site has one camera". */}
          {hostSummary.authFailedIps.length > 0 && (
            <Alert severity="warning" sx={{ fontFamily: "Mulish", mb: 2 }}>
              {hostSummary.authFailedIps.length} host
              {hostSummary.authFailedIps.length === 1 ? "" : "s"} responded to
              ONVIF but rejected the credential
              {credentials.length === 1 ? "" : "s"} supplied. These are likely
              cameras — add their credentials above and scan again:{" "}
              {hostSummary.authFailedIps.join(", ")}
              {hostSummary.lockoutRisk && (
                <div style={{ marginTop: 6 }}>
                  At least one device is counting failed logins. Repeated scans
                  with the wrong credentials can lock it out — confirm the
                  credentials before re-scanning.
                </div>
              )}
            </Alert>
          )}
          {hosts.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ ...headingSx, fontSize: 16, mb: 1 }}>
                {hosts.length} host{hosts.length === 1 ? "" : "s"} discovered
              </Typography>
              <OnvifDiscoveredHostsTable hosts={hosts} />
            </Box>
          )}
          {cameras.length === 0 ? (
            <Alert severity="info" sx={{ fontFamily: "Mulish" }}>
              No ONVIF cameras with an RTSP stream were found.{" "}
              {hostSummary.noStreamIps.length > 0
                ? `Logged in to ${hostSummary.noStreamIps.join(", ")}, but ` +
                  "the device returned no stream address. Add it manually " +
                  "with its RTSP URL."
                : "Check the credentials and subnet, then scan again."}
            </Alert>
          ) : (
            <>
              <Typography sx={{ ...headingSx, fontSize: 16, mb: 1 }}>
                {cameras.length} camera{cameras.length === 1 ? "" : "s"} found
                {selectedCameras.length > 0
                  ? ` — ${selectedCameras.length} selected`
                  : ""}
              </Typography>
              <OnvifResultsTable
                cameras={cameras}
                selectedKeys={selectedKeys}
                selectedProfiles={selectedProfiles}
                onToggle={toggle}
                onToggleAll={toggleAll}
                onProfileChange={setSelectedProfile}
              />
              <Button
                variant="contained"
                onClick={() =>
                  handleSubmitOnvifCameras(
                    selectedCameras,
                    // The list the scan used, in scan order: results identify the
                    // authenticating credential by index into exactly this list.
                    scanCredentials.length > 0 ? scanCredentials : credentials,
                    siteId,
                    setSubmitting,
                    navigate,
                    tunnels.find((t) => t.id === tunnelId)?.server ?? null,
                    onSuccess
                  )
                }
                disabled={selectedCameras.length === 0 || submitting}
                sx={{
                  fontFamily: "Mulish",
                  textTransform: "none",
                  mt: 2,
                  backgroundColor: "#FF8400",
                  "&:hover": { backgroundColor: "#E67300" },
                }}
              >
                {submitting
                  ? "Adding cameras…"
                  : `Add ${selectedCameras.length || ""} selected camera${
                      selectedCameras.length === 1 ? "" : "s"
                    }`}
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
